import { Collapse } from '@mui/material'
import { ButtonTypes } from '@paywithextend/components'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import React, { useMemo, useState } from 'react'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useClickOutside } from 'src/hooks'

import styles from './UtilityToolbarDropdown.style'

export interface UtilityToolbarDropdownProps {
  buttonText: string | React.ReactElement
  dropdown: React.ReactElement<UtilityToolbarDropdownChildProps>
  image?: React.ReactNode
  testId?: string
  selected?: boolean
  showCircleIndicator?: boolean
  disabled?: boolean
  alignment?: 'left' | 'right'
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}

export interface UtilityToolbarDropdownChildProps {
  open?: boolean
  close?: () => void
}

const UtilityToolbarDropdown: React.FC<UtilityToolbarDropdownProps> = ({
  buttonText,
  dropdown,
  image,
  testId,
  selected,
  showCircleIndicator,
  disabled,
  alignment = 'right',
  buttonVariant = 'toolbar',
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)

  const dropdownRef = useClickOutside(() => {
    setIsOpen(false)
  })

  const dropdownControls = useMemo(() => {
    return {
      open: isOpen,
      close: () => setIsOpen(false),
    }
  }, [isOpen])

  const dropdownWithControls = React.isValidElement(dropdown)
    ? React.cloneElement(dropdown, dropdownControls)
    : dropdown

  return (
    <div
      className={cn('UtilityToolbarDropdown', { selected: selected })}
      css={styles}
      ref={dropdownRef}
    >
      <div className="handleContainer">
        <Button
          className={cn('UtilityToolbarDropdown', 'button', {
            selected: selected,
          })}
          data-testid={testId}
          type={ButtonTypes.button}
          variant={buttonVariant}
          dropShadow={false}
          onClick={(): void => {
            setIsOpen(!isOpen)
          }}
          disabled={disabled}
        >
          <span className="flex-row align-center">
            {showCircleIndicator && (
              <span className="UtilityToolbarDropdown circleIndicator" />
            )}
            {image && (
              <span className="UtilityToolbarDropdown image">{image}</span>
            )}
            <span className="UtilityToolbarDropdown label">{buttonText}</span>
            <SvgChevronUp
              width={16}
              height={16}
              fill={'currentColor'}
              className={cn('icon', { open: isOpen })}
            />
          </span>
        </Button>
      </div>
      <div
        className={cn(
          'UtilityToolbarDropdown',
          'dropdownContainer',
          `align-${alignment}`,
        )}
      >
        <Collapse in={isOpen}>{dropdownWithControls}</Collapse>
      </div>
    </div>
  )
}

export default UtilityToolbarDropdown
